import {Component, Inject, Input, LOCALE_ID, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {from, iif, Observable, of} from 'rxjs';
import {concatMap, finalize, groupBy, map, mergeMap, switchMap, tap, toArray} from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';
import {ResponsesService} from '../../../common/services/responses/responses.service';
import {ReponseField} from '../../../common/components/form/models/responsevaluedemat.model';
import {
  BlockForm,
  BlockTypeEnum,
  Field,
  FieldType,
  FormModel,
  OptionsLabel,
  ReponseState,
  ResponseStateInstruction
} from '../../../common/components/form/models';

import {DatePipe} from '@angular/common';
import {ResponseForm} from '../../../common/components/form/models/responsedemat.model';
import {Individu, IndividuExterne, IndividuFactoryService, IndividuService, PubSubService, TypeIndividu} from '@Common/core/services';
import {SubSink} from '@Common/core/utils/subsink';
import {GestionnairesService} from '../../../common/services/individus/gestionnaires.service';
import {roles} from '../../../common/enum/roles';
import {ResponseFormModel} from '../../../common/components/form/models/response-form.model';
import {SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';
import * as FileSaver from 'file-saver';
import {Pagination} from '@Common/widgets/general/pagination';
import {ApplicationEnum} from '@Common/widgets/general/attachments/interfaces/application.enum';
import {CommentModel} from '@Common/widgets/general/comments/interfaces/comment.model';
import {MatTabChangeEvent, MatTabGroup} from '@angular/material/tabs';
import {CommentComponentCallback} from '@Common/widgets/general/comments/comment/comment-callback.interface';
import {GestionnaireDemat} from '../../../common/services/individus/gestionnaire.model';
import {environment} from '@Env/environment';
import {Entite} from '@Services/entite/entite.model';
import {EntiteService} from '@Services/entite/entite.service';
import {constantes} from 'app/demat/common/constantes';
import {FormVisibility} from '../../../common/components/form/models/formdemat.model';
import {DialogData, DialogValidationComponent} from "@Common/widgets/general/dialog-validation";
import {MatDialog} from "@angular/material/dialog";
import {DialogFileComponent} from "../file-preview/dialog-file/dialog-file.component";
import {
  ConditionBlockDemat,
  ConditionBlockResponseDemat
} from "app/demat/common/components/form/models/blockdemat.model";

/**
 * Consultation d'une demande / réponse
 */
@Component({
  selector: 'app-reponse-fiche',
  templateUrl: './reponse-fiche.component.html',
  styleUrls: ['./reponse-fiche.component.scss']
})
export class ReponseFicheComponent implements OnInit, OnDestroy, CommentComponentCallback {
  @Input() response: ResponseForm;
  @Input() displayOpenInNewWindow = false;
  @Input() displayOpinion = false;
  @Input() indexAvis = false;

  responseObjs$: Observable<any>;
  formModel$: Observable<FormModel>;
  formName: string;
  individu: Individu | IndividuExterne;
  fieldType = FieldType;
  blockType = BlockTypeEnum;
  application: ApplicationEnum = ApplicationEnum.demat;
  selectedTabIndex = 0;
  selectedTabIndexAvis = 0;
  tabLabel = '';
  readonly tabLabelGiveOpinion = 'Donner votre Avis';
  /**
   * Gestionnaire du formulaire ?
   * les admin ont aussi accès au formulaire et leurs réponses
   */
  isGestionnaire = false;
  gestionnaireId: number;
  emailsGestionnaires = [];
  emailsAll = [];
  listSvcCmp: Entite[] = [];
  IDX_TAB_MESSAGE = 1;
  IDX_TAB_AVIS_EXTERNE = 2;
  IDX_TAB_AVIS_DEMANDE = 1;
  IDX_TAB_AVIS_LIST = 0;
  onMessage = null;
  subSink = new SubSink();
  extraParams: Map<string, string> = new Map<string, string>();
  constantes = constantes;
  lastInstructionState: ResponseStateInstruction;
  @ViewChild(MatTabGroup, { static: false }) matTabGroup: MatTabGroup;

  constructor(@Inject(LOCALE_ID) private locale: string,
              private dialog: MatDialog,
              private route: ActivatedRoute,
              private individuService: IndividuFactoryService,
              private pubSubSvc: PubSubService,
              private snackSvc: SnackBarToastyService,
              private gestionnaireSvc: GestionnairesService,
              private responsesSvc: ResponsesService,
              private entiteSvc: EntiteService) {
  }

  ngOnInit(): void {
    this.onMessage = this.route.snapshot.paramMap.get('message');
    this.lastInstructionState = this.response?.last_instruction_obj?.state;
    this._getResponses();
    this._fetchSvcCmp();
  }

  titleTab() {
    if (this.response) {
      return `Demande n° ${this.response.reference}`;
    }
    return 'Demande';
  }
  hasAdministrativeBlock(form: FormModel) {
    return this._hasAdministrativeBlock(form);
  }
  wordingMessagesTab() {
    let wording = `Cet espace vous permet d'échanger avec les gestionnaires du formulaire de demande "${this.response?.form_obj?.nom}"`;
    if (this.isGestionnaire) {
      wording = `Cet espace vous permet d'échanger avec le demandeur (message public) ou les gestionnaires (message public ou interne) du formulaire de demande "${this.response?.form_obj?.nom}"`;
    }
    return wording;
  }

  /**
   * L'onglet Messages n'apparait pas sur une demande en brouillon et qui n'a pas encore été envoyée (sans instruction)
   * /!\ Demat-ext : choix pour l'instant pour les formulaires externes de ne pas afficher l'onglet de message pour réduire le risque (Comment & co du common et sur individu)
   * @param {ResponseForm} response
   */
  canDisplayMessages(response: ResponseForm) {
    if (! response) {
      return false;
    }
    if (this.response?.form_obj?.visibility === FormVisibility.Intern) { // on n'affiche l'onglet Messages que pour les forms internes
      if (this.individu.user === response.user_obj.user || this.isGestionnaire) {
        return !!(response.state !== ReponseState.draft ||
          response.state === ReponseState.draft && response.last_instruction_obj);
      }
    }
    return false;
  }

  /**
   * Chip état demande
   * @param response
   */
  stateResponseChip(response: ResponseForm) {
    return this.responsesSvc.stateDisplay(response?.state);
  }

  /**
   * Chip état instruction
   * @param response
   */
  stateInstructionChip(response: ResponseForm) {
    return this.responsesSvc.stateInstructionDisplayColor(response);
  }
  emitNewResponseStateInstruction(stateInstruction: ResponseStateInstruction) {
    this.lastInstructionState = stateInstruction;
    const lastInstructionObj = this.response.last_instruction_obj;
    if (lastInstructionObj || lastInstructionObj !== null) {
      this.response.last_instruction_obj.state = stateInstruction;
    }
  }

  _realignInkBar() {
    if (this.matTabGroup) {
      this.matTabGroup.realignInkBar();
    }
  }
  /**
   * Permet de numéroter les blocks : 01 block1, 02 block2, ..., 12 block12
   * @param {number} indexBlock : indice du block courant
   */
  getBlockPrefixDesign(indexBlock: number) {
    return String(indexBlock + 1).padStart(2, '0');
  }

  /**
   * Le label du champ
   * @param {ReponseField} item
   */
  getLabel(item: ReponseField) {
    return (<Field>item.field).options_obj.label;
  }

  /**
   * Exclure les types de champ souhaité pour affichage quand un traitement spécifique doit être effectué
   * @param {ReponseField} item
   */
  filterTypeOfField(item: ReponseField) {
    const controlsType = [FieldType.TypeTextarea, FieldType.TypeFile, FieldType.TypeLabel, FieldType.TypeAvis];
    const controlType = (<Field>item.field).options_obj?.controlType;
    return (controlsType.includes(controlType));
  }

  /**
   * Valeur du champ Label
   * @param {ReponseField} item
   */
  getValueLabel(item: ReponseField) {
    const field = item.field as Field;
    if (field.options_obj.controlType === FieldType.TypeLabel) {
      const options = field.options_obj as OptionsLabel;
      if (options.content) {
        return options.content;
      }
    }
    return '';
  }
  /**
   * Valeur à afficher selon le type du champ
   * @param {ReponseField} item
   */
  getValue(item: ReponseField) {
    if ((<Field>item.field).options_obj.controlType === FieldType.TypeDate) {
      const dt: DatePipe = new DatePipe(this.locale);
      return dt.transform(item.value, 'dd/MM/yyyy');
    }
    if ((<Field>item.field).options_obj.controlType === FieldType.TypeCheckbox) {
      if (item.value) {
        let values = '';
        item.value.forEach(_option => {
          if (_option.value) {
            values = `${_option.key}, ${values}`;
          }
        });
        return values.substring(0, values.length - 2);
      }
      return '';
    }
    if ((<Field>item.field).options_obj.controlType === FieldType.TypeSvcCmp) {
      const entite = this.listSvcCmp.find(_entite => _entite.entite === item.value);
      return entite?.entite_obj['name'] || '/';
    }
    if ((<Field>item.field).options_obj.controlType === FieldType.TypeLabel) {
      if (item.value) {
        return item.value;
      }
      return '';
    }
    if ((<Field>item.field).options_obj.controlType === FieldType.TypeAvis) {
      if (item) {
        return item;
      }
      return '';
    }
    return item.value;
  }
  /**
   * response.value est un tableau qui contient un objet contenant tout les valeurs avec key = id champ et value = valeur champ
   * On récupere l'objet value (resp)
   * On map resp et on le divise en plusieur objet value (mapValues)
   * On merge response['responses_value'] avec mapValues (pour recuperer l'id value s'il existe)
   */
  onSubmitBlockAdmin(response: ResponseFormModel) {
    this.pubSubSvc.publish('loading', true);
    const resp = response.value[0];
    let mapValues = [];
    Object.entries(resp).map(([key, value]) => {
      const obj = {
        responseId: this.response.id,
        field: Number(key),
        value: value
      };
      mapValues = [...mapValues, obj];
      return obj;
    });
    const mergeValuesAndResponsesValues = mapValues.map((item, i) => Object.assign({}, item, response['responses_value'][i]));
    this.subSink.sink = this.responsesSvc
      .sendValuesBlockAdmin(mergeValuesAndResponsesValues)
      .pipe(
        switchMap(res => {
          const isEmptyFilesToUploadObject = Object.keys(response['filesToUploadObject']).length > 0;
          return iif(
            () => isEmptyFilesToUploadObject,
            this.responsesSvc.uploadFiles(response['filesToUploadObject'], this.response.id, true),
            of(res)
          );
        }),
        finalize(() => this.pubSubSvc.publish('loading', false))
      )
      .subscribe(_result => {
        this.snackSvc.toasty('Traitement suivi administratif', 'Le suivi administratif a bien été enregistré', typeToast.success);
        this._getResponses();
      });
  }
  downloadFile(value) {
    console.log('value', value);
    this.subSink.sink = this.responsesSvc.getFileResponse(value).subscribe(v => FileSaver.saveAs(v, value.value));
  }
  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  /**
   * Implémentations CommentComponentCallback : getLink & getTitle
   * @param comment
   * @param object_id
   */
  getLink(comment: CommentModel, object_id: number): Observable<string> {
    return new Observable(observer => {
      let urlToMessage = `/demande/visualiser/${this.response.uuid}/messages`;
      if (environment.env !== 'dev') {
        urlToMessage = `/demat${urlToMessage}`;
      }
      observer.next(urlToMessage);
      observer.complete();
    });
  }
  getLinkLabel(comment: CommentModel, object_id: number): Observable<string> {
    return new Observable(observer => {
      const linkLabel = 'Accéder aux messages';
      observer.next(linkLabel);
      observer.complete();
    });
  }
  getTitle(comment: CommentModel, object_id: number): Observable<string> {
    return new Observable(observer => {
      const title = `[DEMAT] Nouveau message sur la demande "${this.response?.form_obj?.nom}"`;
      observer.next(title);
      observer.complete();
    });
  }

  /**
   * Liste des services et composantes pour le champ SvpCmp
   * @private
   */
  private _fetchSvcCmp() {
    this.extraParams.set('application', constantes.application.toString());
    this.subSink.sink = this.entiteSvc
      .listAllItems(this.extraParams)
      .subscribe((_data: Pagination) => this.listSvcCmp = _data.list);
  }
  /**
   * Charge le connecté et vérifie s'il est gestionnaire du formulaire
   * Charge les mails des gestionnaires ou gestionnaire + demandeur
   * Charge le mail générique parmi les gestionnaires s'il existe
   * @private
   */
  private _getGestionnaireAndMailsGestionnaires(response: ResponseForm) {
    return this.individuService.individu$
      .pipe(
        concatMap((_individu: Individu | IndividuExterne) => {
          this.individu = _individu;
          return this.gestionnaireSvc.gestionnaireFormulaire(this.response.form_obj.uuid, _individu.user);
      }),
        concatMap((_gest: GestionnaireDemat[]) => {
          if (this.individu.type === TypeIndividu.intern) {
            const _service = this.individuService.individuService() as IndividuService;
            if (_gest.length > 0 || _service.hasRole(this.individu as Individu, roles.admin)) {
              this.gestionnaireId = _gest.length > 0 ? _gest[0]?.id : null;
              this.isGestionnaire = true;
            }
            return this.gestionnaireSvc.gestionnairesFormulaire(response.form_obj.uuid);
          } else {
            this.isGestionnaire = false;
            return of([]);
          }
        }),
        concatMap((_gestionnaires) => {
          let emailsGest = _gestionnaires.map(_gest => _gest.role.individu.email_pro);
          if (response.form_obj.email_generic) {
            emailsGest = [...emailsGest, ...[response.form_obj.email_generic]];
          }
          this.emailsGestionnaires = Array.from(new Set(emailsGest));
          this.emailsAll = Array.from(new Set([...[response.user_obj.email_pro], ...emailsGest]));
          return of(response);
        }));
  }
  /**
   * Nom formulaire et mail demandeur
   * @param {ResponseForm} response
   * @private
   */
  private _initResponseMeta(response: ResponseForm) {
    this.formName = response.form_obj.nom;
  }

  /**
   * Les blocs et champs pour la visualisation
   * Init si le connecte gestionnaire
   * Init les mails des gestionnaires et mail générique et tous (demandeur compris)
   * Détection du bloc administratif
   * Positionnement sur l'onglet Message le cas échéant
   * @param {ResponseForm} reponse
   * @private
   */
  private _getResponseObj(reponse: ResponseForm) {
    return new Observable(observer => {
      this._initResponseMeta(reponse);
      of(reponse).pipe(
        concatMap((_reponse) => this._getGestionnaireAndMailsGestionnaires(_reponse)),
        switchMap(_reponse => {
          this.formModel$ = this._getFormModelForBlockAdminResponse(_reponse);
          this.subSink.sink = this.formModel$.subscribe(_form => {
            if (_form.visibility === FormVisibility.Intern) {
              this._setTabFormInterne(_form, this.isGestionnaire);
            }
            if (_form.visibility === FormVisibility.Extern) {
              this._setTabFormExterne(_form, this.isGestionnaire);
            }
          });
          return this._groupAndToArray(_reponse);
        })).subscribe(_rp => observer.next(_rp));
    });
  }
  /**
   * Arrivée soit par input() soit par url
   * @private
   */
  private _getResponses() {
    let uuid: string;
    if (this.response) { // @Input()
      this.responseObjs$ = this._getResponseObj(this.response);
    } else { // via url
      uuid = this.route.snapshot.paramMap.get('uuid');
      if (this.route.snapshot.url[1].path === 'visualiser-sollicite') {
        this._getResponseFieldsValuesSolliciteByApi(uuid);
      } else {
        this._getResponseFieldsValuesByApi(uuid);
      }

    }
  }
  /**
   * Obtention de la réponse / values par API
   * @param {string} uuid
   * @private
   */
  private _getResponseFieldsValuesByApi(uuid: string) {
    this.responseObjs$ = this.responsesSvc.get(uuid).pipe(
            tap(reponse => this.response = reponse),
            concatMap((_reponse) => this._getResponseObj(_reponse))
    );
  }

  private _getResponseFieldsValuesSolliciteByApi(uuid: string) {
    this.responseObjs$ = this.responsesSvc.getByUUIDForSollicite(uuid).pipe(
      tap(reponse => this.response = reponse),
      concatMap((_reponse) => this._getResponseObj(_reponse))
    );
  }
  /**
   * Obtient uniquement les blocs publics pour l'affichage des réponses demandeur
   * @param {ResponseForm} response
   */
  private _getOnlyBlockDemandeur(response: ResponseForm) {
    let blockAdmin: BlockForm = <BlockForm> {id: -1};
    if ('block_admin' in response.form_obj) {
      blockAdmin = response.form_obj['block_admin'];
    }
    return response.response_objs
      .filter(_response => _response.block !== blockAdmin.id)
      .sort((a, b) => a['block_position'] - b['block_position']);

  }
  /**
   * Groupement par blocs / champs
   * @param {ResponseForm} response
   * @private
   */
  private _groupByGroupAndResponseFields(response: ResponseForm) {
    const responses = this._getOnlyBlockDemandeur(response);
    return from(responses).pipe(
      groupBy(
        (f: ReponseField) => f.block,
        p => p
      ),
      mergeMap(group => group.pipe(toArray()))
    );
  }
  private _groupAndToArray(response) {
    return of(response).pipe(
      switchMap(_response => this._groupByGroupAndResponseFields(_response)),
      toArray()
    );
  }
  /**
   * Le bloc administratif de la réponse
   * @param {ResponseForm} response
   */
  private _getFormModelForBlockAdminResponse(response: ResponseForm) {
    return of(response.form_obj)
      .pipe(
        map(f => {
          f.form_responses = [response];
          f.blocks = [f['block_admin']];
          return f;
        }));
  }

  /**
   * Calcul du décalage si apparition des onglets "Suivi administratif" et/ou "Donner votre avis", ils sont forcément devant Message / Avis le cas échéant
   * Demande / [Suivi administratif] / [Donner votre avis] / le reste des onglets
   * @param {FormModel} form
   * @param {boolean} isGestionnaire
   * @private
   */
  private _calculate_index_tab(form: FormModel, isGestionnaire: boolean) {
    let count_idx_message = 0;
    if (this._hasAdministrativeBlock(form, isGestionnaire)) {
      count_idx_message += 1;
    }
    if (this.displayOpinion) {
      count_idx_message += 1;
    }
    return count_idx_message;
  }
  /**
   * Détermine l'index de l'onglet Messages selon si bloc administratif ou non
   * pour un formulaire "interne"
   * Onglets possible avec les onglets [optionnels] - ce qui donne une multitude de combinaisons d'affichage des onglets
   * Demande / [Suivi adm] / [Donner votre avis] / Message | [Avis (gestion)]
   * @param {FormModel} form
   * @param {boolean} isGestionnaire
   * @private
   */
  private _setTabFormInterne(form: FormModel, isGestionnaire: boolean) {
    let count_idx = 0;
    this.IDX_TAB_MESSAGE = 1;
    this.IDX_TAB_AVIS_EXTERNE = 2;
    count_idx = this._calculate_index_tab(form, isGestionnaire);
    this.IDX_TAB_MESSAGE += count_idx;
    this.IDX_TAB_AVIS_EXTERNE += count_idx;
    this._goTabMessage();
  }
  /**
   * Pour un formulaire "externe", il n'y a pas l'onglet Messages
   * Onglets possible :
   * Demande / [Suivi adm] / [Donner votre avis] / [Avis (gestion)]
   * @param {FormModel} form
   * @param {boolean} isGestionnaire
   * @private
   */
  private _setTabFormExterne(form: FormModel, isGestionnaire: boolean) {
    let count_idx = 0;
    this.IDX_TAB_AVIS_EXTERNE = 1;
    count_idx = this._calculate_index_tab(form, isGestionnaire);
    this.IDX_TAB_AVIS_EXTERNE += count_idx;
    this._goTabMessage();
  }
  /**
   * Bascule sur l'onglet Messages
   * @private
   */
  private _goTabMessage() {
    if (this.onMessage) {
      this.selectedTabIndex = this.IDX_TAB_MESSAGE;
    }
    if (this.indexAvis) {
      this.selectedTabIndex = this.IDX_TAB_AVIS_EXTERNE;
    }
  }
  private _hasAdministrativeBlock(form: FormModel, isGestionnaire = this.isGestionnaire) {
    return form && form['block_admin']?.fields_objs?.length > 0 && isGestionnaire;
  }

  selectedTabValue($event: MatTabChangeEvent) {
    this.tabLabel = $event.tab.textLabel;
  }

  /**
   * Uniquement les PJ ayant un nom de fichier
   * @param {Pj[]} pjs
   * @return {Pj[]}
   */
  filterPj(pjs: any) {
    return pjs.filter(pj => pj.value != null);
  }

 getFileExtension(filePath: string): boolean {
    const lastDotIndex = filePath.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return false;
    }
    const extension = filePath.substring(lastDotIndex + 1).toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'];
    const pdfExtension = 'pdf';

    return extension === pdfExtension || imageExtensions.includes(extension);
  }

  /**
   * Visualisation des PJ selon le filtre
   * @param {Pj[]} pjs
   */
  visualiser(pjs: any) {
    if (pjs && pjs.length > 0) {
      const dialogRef = this.dialog.open(DialogFileComponent, {
        data: this.filterPj(pjs)
      });
      dialogRef.updatePosition({top: '20px'});
    } else {
      const data = new DialogData();
      data.title = 'Aucune PJ transmises';
      data.message = 'Il n\'y a pas de PJ transmises à visualiser pour la dernière année';
      data.buttonTextTrue = 'Fermer';
      data.buttonTextFalse = '';

      this.dialog.open(DialogValidationComponent, { data: data, disableClose: false});
    }
  }

  getConditionBLock(resp: any): ConditionBlockDemat {
    const block = this.response.condition_block_objs.find(itm => itm.block === resp[0]?.block);
    const blockDefault: ConditionBlockDemat = {is_active: false};
    return block || blockDefault;
  }
  getConditionBLockValue(resp: any): ConditionBlockResponseDemat {
    const block = this.response.condition_block_value_objs.find(itm => itm.block === resp[0]?.block);
    const blockDefault: ConditionBlockResponseDemat = {display: true};
    return block || blockDefault;
  }
}
